import React, { useMemo, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCDropDownItem, RCCGrid, RCCHookToast, RCCIcon, RCCInput, RCCRangeSlider, RCCSelectOptionsCard, RCCServicesApi, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import FiltersContainer from "src/components/FiltersContainer";
import Loader from "src/components/Loader";
import LovSectors from "src/components/Lov/Sectors";
import LovUsers from "src/components/Lov/Users";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import extractSingleValueFromArrayOfObject from "src/utils/extractSingleValueFromArrayOfObject";
import formatDate from "src/utils/formatDate";
import FiltersTagsContainer from "src/components/FiltersTagsContainer";
import { IRefFiltersTagsContainer, TagsTypes } from "src/components/FiltersTagsContainer/interface";
import { useAuth } from "src/context/Auth/context";
import { getLocalStorage, setLocalStorage } from "src/utils/localStorage";
import { getCookie, setCookie } from "src/utils/cookies";

import ButtonExpandGroupers from "src/components/ButtonExpandGroupers";
import { useHistory, useLocation, useParams } from "react-router-dom";
import LovMaps from "src/components/Lov/Maps";
import LovPerspectives from "src/components/Lov/Perspectives";
import LovObjectives from "src/components/Lov/Objectives";
import LovIndicators from "src/components/Lov/Indicators";
import DropdownWithBackground from "src/components/DropdownWithBackground";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import putUrlSearchParams from "src/utils/putUrlSearchParams";
import LovGrouperActions from "src/components/Lov/GrouperActions";
import AppIcons from "src/components/AppIcons";
import Reports from "src/components/Reports";
import ReportGroupers from "src/components/Reports/interface";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import { v4 } from "uuid";
import stringToDate from "src/utils/stringToDate";
import ScreenStandardGroup from "src/Interfaces/screenStandardGroup";
import IFiltersActionsPanel, { ActionDelayed, ActionGroupers, ActionSituation, ActionTraceability, IGroupersActions, IGroupersActionsSelect, IRefActionsPanelModalAddChangeDelete } from "./interface";
import ModalAddChangeDelete from "./components/modalAddChangeDelete";
import ModalActionsHierarchyMaps, {IRefModalActionsHierarchyMaps} from "./components/modalActionsHierarchyMaps";
import GrouperOfActions from "./grouperOfActions";

const ActionsPanel: React.FC = () => {
	const params = useParams<{
        actionId: string;
    }>();

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);
	
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { user } = useAuth();

	const modalAddRef = useRef<IRefActionsPanelModalAddChangeDelete>(null);
	const modalHierarchyMaps = useRef<IRefModalActionsHierarchyMaps>(null);

	// default: card
	const [layoutList, setLayoutList] = useState<"card" | "list">(getLocalStorage(`ActionScreenLayoutList-${user.efa005Id}`) === "list" ? "list" : "card");

	const history = useHistory();

	const optionsSituation = [{
		label: translate("Cancelada"),
		value: ActionSituation.CANCELED
	},{
		label: translate("Em Rascunho"),
		value: ActionSituation.DRAFT
	},{
		label: translate("Concluída"),
		value: ActionSituation.FINISHED
	},{
		label: translate("Em andamento"),
		value: ActionSituation.IN_PROGRESS
	},{
		label: translate("Não Iniciada"),
		value: ActionSituation.NOT_STARTED
	}];

	const filtersTagsRef = useRef<IRefFiltersTagsContainer>(null);
	const [showTagsFilters, setShowTagsFilters] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const [myActions, setMyActions] = useState(!!objQueryParams?.myActions);
	const [filters, setFilters] = useState<IFiltersActionsPanel>({ 
		f: objQueryParams?.f || "", 
		responsible: objQueryParams?.responsible || "",
		authority: objQueryParams?.authority || "",
		consulted: objQueryParams?.consulted || "",
		informed: objQueryParams?.informed || "",
		sector: objQueryParams?.sector || "",
		situation: objQueryParams?.situation || [{
			label: translate("Concluída"),
			value: ActionSituation.FINISHED
		},{
			label: translate("Em andamento"),
			value: ActionSituation.IN_PROGRESS
		},{
			label: translate("Não Iniciada"),
			value: ActionSituation.NOT_STARTED
		}],
		startDate: objQueryParams?.startDate ? stringToDate(objQueryParams.startDate) : null,
		endDate: objQueryParams?.endDate ? stringToDate(objQueryParams?.endDate) : null,
		tags: objQueryParams?.tags || "",
		map: objQueryParams?.map || "",
		perspective: objQueryParams?.perspective || "",
		objective: objQueryParams?.objective || "",
		indicator: objQueryParams?.indicator || "",
		filterOrigin: objQueryParams?.filterOrigin || "",
		project: objQueryParams?.project || "",
		percConclusion: [0, 100],
		actionId: params.actionId || ""
	});
	const [percRangeSlider, setPercRangeSlider] = useState<number[] | undefined>(filters.percConclusion);
	const handleChangeSlider = (_: Event, newValue: number | number[]) => setPercRangeSlider(newValue as any);
	const { handleSubmit : handleSubmitFilters, register : registerFilters, watch: watchFilters, control: controlFilters, reset: resetFilters, setValue: setValueFilters } = useForm();
	const submitFilters = async (data: any) => {
		const dataForm = data;
		if(data.situation)
			dataForm.situation = extractSingleValueFromArrayOfObject("value", data.situation)?.toString();
		
		if(typeof data.authority === "object")
			dataForm.authority = data.authority?.value || "";
		if(typeof data.consulted === "object")
			dataForm.consulted = data.consulted?.value || "";
		if(typeof data.informed === "object")
			dataForm.informed = data.informed?.value || "";
		if(typeof data.responsible === "object")
			dataForm.responsible = data.responsible?.value || "";
		if(typeof data.sector === "object")
			dataForm.sector = data.sector?.value || "";
		if(typeof data.map === "object")
			dataForm.map = data.map?.value || "";
		if(typeof data.perspective === "object")
			dataForm.perspective = data.perspective?.value || "";
		if(typeof data.objective === "object")
			dataForm.objective = data.objective?.value || "";
		if(typeof data.indicator === "object")
			dataForm.indicator = data.indicator?.value || "";

		if(myActions)
			dataForm.responsible = user.efa005Id.toString();

		dataForm.percConclusion = percRangeSlider;

		dataForm.filterOrigin = "";
		dataForm.actionId = "";

		putUrlSearchParams(dataForm);
		setFilters(dataForm);
	};
	const submitFiltersTags = async () => {
		setFilters((prevState) => {
			const updateState = {...prevState};
			updateState.tags = filtersTagsRef.current?.response || "";
			return updateState;
		});
	};

	const buttonSubmitFormGroupersRef = useRef<HTMLButtonElement>(null);
	const { handleSubmit : handleSubmitGroupers, control: controlGroupers } = useForm();
	const [defaultGrouper, setDefaultGrouper] = useState<ActionGroupers | undefined>(getCookie(ScreenStandardGroup.ACTION) as ActionGroupers);
	const [groupersSelect, setGroupersSelect] = useState<IGroupersActionsSelect>({
		group: objQueryParams?.group as ActionGroupers || defaultGrouper || ActionGroupers.MAP,
		origin: "",
		delayed: objQueryParams?.delayed as ActionDelayed || ""
	});
	const submitGroupers = async (data: any) => {
		setGroupersSelect({
			...data
		});
	};

	const setAsDefaultGrouper = async () => {
		const {group} = groupersSelect;
		try{
			await RCCServicesApi.postRequest({
				method: "Actions/SetDefaultGrouper",
				body: {
					group
				}
			});
			setDefaultGrouper(group);
			setCookie(ScreenStandardGroup.ACTION, group);
			addToast({
				color: "green",
				description: translate("Agrupador padrão atualizado com sucesso"),
				title: translate("Sucesso")
			});
		}catch(err){
			standardErrorMessages(err, translate("Erro ao alterar agrupador padrão"), addToast);
		}

	};

	const situationFilter = (situation: any) => {
		if(Array.isArray(situation))
			return extractSingleValueFromArrayOfObject("value", situation)?.toString();
		if(situation)
			return situation;
		return  "";
	};

	const { errorFetching, data, loading, loadData } = useEffectFetchData<IGroupersActions[]> (
		"Actions/ListGroupers", 
		{ 
			...groupersSelect,
			f: filters.f || "",
			responsible: filters.responsible || "",
			authority: filters.authority || "",
			consulted: filters.consulted || "",
			informed: filters.informed || "",
			sector: filters.sector || "",
			situation: situationFilter(filters.situation),
			startDate: filters.startDate ? formatDate(filters.startDate) : "",
			endDate: filters.endDate ? formatDate(filters.endDate) : "",
			tags: filters.tags || "",
			map: filters.map || "",
			perspective: filters.perspective || "",
			objective: filters.objective || "",
			indicator: filters.indicator || "",
			filterOrigin: filters.filterOrigin || "",
			startPerc: filters.percConclusion[0] || 0,
			endPerc: filters.percConclusion[1] || 100,
			actionId: filters.actionId || ""
		 },
		 undefined,
		 [groupersSelect, filters],
		 true
	);

	const onCallbackAdd = () => loadData();

	const Groupers = useMemo(() => loading ? (
		<Loader />
	) : (
		<>
			{errorFetching ? (
				<RCCAlert 
					color="red"
					title={translate("Atenção")}
					description={translate("Erro ao tentar encontrar resultados para esta pesquisa")}
				/>
			): (
				<>
					{!data || data.length === 0 ? (
						<RCCAlert 
							title={translate("Atenção")}
							description={translate("Nenhum agrupador encontrado")}
						/>
					): (
						<>
							{data.map((grouper) => (
								<GrouperOfActions 
									key={`${grouper.id}-${grouper.id2}-${grouper.id3}-${grouper.id4}-grouper-actions`}
									filters={filters}
									grouper={grouper}
									groupersSelect={groupersSelect}
									layoutList={layoutList}
									onCallbackAdd={onCallbackAdd}
								/>
							))}
						</>
					)}
				</>
			)}
		</>
	), [loading, errorFetching, data, layoutList]);

	const mapValueWatch = watchFilters("map");
	const perspectiveValueWatch = watchFilters("perspective");

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Painel de Ações")}
					icon={() => <AppIcons name="actions" />}
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<DropdownWithBackground
							position="leftEnd"
							callerComponent={
								<RCCButton
									icon={{name: "plus", color: "#FFF", size: 18}}
									color="purple"
									layout="isRounded"
									size="md"
								/>
							}
						>
							<RCCDropDownItem
								selfEnclose
								onClick={() => modalAddRef.current?.openModalAddOrChange(undefined, () => onCallbackAdd())}
							>
								<RCCIcon name="plus" />
								{translate("Ação")}
							</RCCDropDownItem>
							<RCCDropDownItem
								selfEnclose
								onClick={() => modalHierarchyMaps.current?.openModalActionsHierarchyMaps()}
							>
								<RCCIcon name="map" />
								{translate("Ação mapa estratégico")}
							</RCCDropDownItem>
						</DropdownWithBackground>
					</CheckPermission>
					<RCCTooltip
						text="Kanban"
						color="grey"
					>
						<RCCButton
							icon={{
								name: "kanban", 
								color: "#FFF", 
								size: 16
							}}
							color="grey"
							layout="isRounded"
							size="md"
							onClick={() => history.push("/actions/kanban")}
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Matriz de priorização")}
						color="grey"
					>
						<RCCButton
							icon={{
								name: "dashboard", 
								color: "#FFF", 
								size: 16
							}}
							color="grey"
							layout="isRounded"
							size="md"
							onClick={() => history.push("/actions/matrix")}
						/>
					</RCCTooltip>
					<ButtonExpandGroupers />
					<RCCTooltip
						text={layoutList === "card" ? translate("Alterar para lista") : translate("Alterar para cards")}
						color="grey"
					>
						<RCCButton
							icon={{
								name: layoutList === "card" ? "list" : "table", 
								color: "#FFF", 
								size: 16
							}}
							color="grey"
							layout="isRounded"
							size="md"
							onClick={() => {
								if(layoutList === "card"){
									setLayoutList("list");
									setLocalStorage(`ActionScreenLayoutList-${user.efa005Id}`, "list");
									return;
								}
								setLocalStorage(`ActionScreenLayoutList-${user.efa005Id}`, "card");
								setLayoutList("card");
							}}
						/>
					</RCCTooltip>
					<Reports grouper={ReportGroupers.ACTIONS}/>
					<RCCTooltip
						text={`${translate("Filtros")} Tags`}
						color={showTagsFilters || filters.tags ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "tags", color: "#FFF", size: 16}}
							color={showTagsFilters || filters.tags ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowTagsFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || 
							filters.authority ||
							filters.consulted || 
							filters.endDate || 
							filters.f || 
							filters.informed ||
							filters.responsible || 
							filters.sector || 
							filters.situation || 
							filters.startDate
						 ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={
								showFilters ||
								filters.authority ||
								filters.consulted || 
								filters.endDate || 
								filters.f || 
								filters.informed ||
								filters.responsible || 
								filters.sector || 
								filters.situation || 
								filters.startDate
							 ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>

				{/* Filters */}
				<FiltersContainer
					showFilters={showFilters}
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => {
						setPercRangeSlider([0, 100]);
						resetFilters({ 
							f: "", 
							responsible: { value: "", label: ""},
							authority: { value: "", label: ""},
							consulted: { value: "", label: ""},
							informed: { value: "", label: ""},
							sector: { value: "", label: ""},
							situation: [{
								label: translate("Concluída"),
								value: ActionSituation.FINISHED
							},{
								label: translate("Em andamento"),
								value: ActionSituation.IN_PROGRESS
							},{
								label: translate("Não Iniciada"),
								value: ActionSituation.NOT_STARTED
							}],
							startDate: null,
							endDate: null,
							objective: "",
							perspective: "",
							map: "",
							indicator: { value: "", label: ""},
							percConclusion: [0, 100]
						});
						putUrlSearchParams({ 
							f: "", 
							responsible: "",
							authority: "",
							consulted: "",
							informed: "",
							sector: "",
							situation: "2,1,0",
							startDate: null,
							endDate: null,
							objective: "",
							perspective: "",
							map: "",
							indicator: "",
							percConclusion: "0,100"
						});
					}}
				>
					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<DateTimePickerRange
								control={controlFilters}
								label={translate("Período")}
								labelSeparator={translate("até")}
								nameInit = "startDate"
								nameEnd = "endDate"
								defaultValueEnd={filters.endDate}
								defaultValueInit={filters.startDate}
								type="datePicker"
								setValue={setValueFilters}
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="authority"
								label={translate("Autoridade")}
								defaultValue={filters.authority}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="responsible"
								label={translate("Responsável")}
								defaultValue={filters.responsible}
								disabled={myActions}
								isClearable
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="consulted"
								label={translate("Consultado")}
								defaultValue={filters.consulted}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovUsers 
								control={controlFilters}
								name="informed"
								label={translate("Informado")}
								defaultValue={filters.informed}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovSectors 
								control={controlFilters}
								name="sector"
								label={translate("Setor")}
								defaultValue={filters.sector}
								isClearable
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovMaps 
								control={controlFilters}
								name="map"
								label={translate("Mapas")}
								defaultValue={filters.map || undefined}
								onChangeCapture={() => {
									setValueFilters("perspective" , undefined);
									setValueFilters("objective" , undefined);
								}}
								submit="value and label"
								cacheOptions={false}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovPerspectives 
							  key={mapValueWatch?.value || mapValueWatch || v4()}
								control={controlFilters}
								name="perspective"
								label={translate("Perspectivas")}
								defaultValue={filters.perspective}
								extraParametersOnLoad={{
									map: mapValueWatch?.value || mapValueWatch || ""
								}}
								onChangeCapture={() => {
									setValueFilters("objective" , undefined);
								}}
								submit="value and label"
								disabled={!mapValueWatch}
								cacheOptions={false}
								isClearable
							/>
						</RCCGrid>

						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovObjectives 
								key={perspectiveValueWatch?.value || perspectiveValueWatch || v4()}
								control={controlFilters}
								name="objective"
								label={translate("Objetivo")}
								defaultValue={filters.objective}
								extraParametersOnLoad={{
									map: mapValueWatch?.value || mapValueWatch || "",
									perspective: perspectiveValueWatch?.value || perspectiveValueWatch || ""
								}}
								submit="value and label"
								disabled={!mapValueWatch || !perspectiveValueWatch}
								cacheOptions={false}
								isClearable
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<LovIndicators 
								control={controlFilters}
								name="indicator"
								label={translate("Indicadores")}
								defaultValue={filters.indicator}
								isClearable
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<Select 
								control={controlFilters}
								name="situation"
								label={translate("Situação")}
								defaultValue={filters.situation || [{
									label: translate("Concluída"),
									value: ActionSituation.FINISHED
								},{
									label: translate("Em andamento"),
									value: ActionSituation.IN_PROGRESS
								},{
									label: translate("Não Iniciada"),
									value: ActionSituation.NOT_STARTED
								}]}
								options={optionsSituation}
								isMulti
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
							style={{padding: "0 8px"}}
						>
							<RCCRangeSlider
								control={controlFilters}
								name="percConclusion"
								color="purple"
								label={translate("Conclusão")}
								value={percRangeSlider}
								min={0}
								max={100}
								onChange={handleChangeSlider as any}
								valueLabelDisplay="on"
								valueLabelFormat={(value) => `${value}%`}
								styleInline=".MuiSlider-valueLabel > span > span{ color: var(--input-font-color); }"
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="f" 
								label={translate("Pesquisar")}
								register={registerFilters}
								defaultValue={filters.f}
							/>
						</RCCGrid>
					</RCCGrid>            
				</FiltersContainer>
			
				{/* Filters Tags */}
				<FiltersTagsContainer 
					ref={filtersTagsRef}
					onSubmitFilters={submitFiltersTags}
					tagType={TagsTypes.ACTIONS}
					showFilters={showTagsFilters}
				/>

				{/* Groupers Select -> Origin -> Delayed */}
				<form onSubmit={handleSubmitGroupers(submitGroupers)} style={{marginBottom: "1rem"}}>
					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							lg={3}
							alignItems="baseline"
						>
							<LovGrouperActions 
								control={controlGroupers}
								name="group"
								defaultValue={groupersSelect.group}
								onChangeCapture={() => buttonSubmitFormGroupersRef.current?.click()}
								styleInline="width: auto; flex: 1;"
							/>
							<RCCTooltip
								color="grey"
								text={translate("Definir como agrupador padrão")}
							>
								<RCCButton 
									color="transparent"
									icon={{
										name: "checkBookmark",
										color: "var(--grey)"
									}}
									onClick={setAsDefaultGrouper}
								/>
							</RCCTooltip>
						</RCCGrid>
						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							lg={3}
						>
							<Select 
								control={controlGroupers}
								name="origin"
								label={translate("Origem")}
								options={[
									{ value: ActionTraceability.ACTION, label: `${translate("Sem origem")}`},
									{ value: ActionTraceability.INICIATIVE_MAP, label: `${translate("Iniciativa")} - ${translate("Mapa")}` },
									{ value: ActionTraceability.FCA_INDICATOR, label: `${translate("FCA")} - ${translate("Indicador")}` },
									{ value: ActionTraceability.MEETING, label: `${translate("Reunião")}` },
									{ value: ActionTraceability.PROJECT, label: `${translate("Projeto")}` },
									{ value: ActionTraceability.RISK, label: `${translate("Risco")}` },
									{ value: ActionTraceability.OCCURRENCE, label: `${translate("Ocorrência")}` },
									{ value: ActionTraceability.INICIATIVE_OKR, label: `${translate("Iniciativa")} - ${translate("OKR")}` }
								]}
								isClearable
								defaultValue={groupersSelect.origin}
								onChangeCapture={() => buttonSubmitFormGroupersRef.current?.click()}
							/>
						</RCCGrid>

						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={4}
							lg={3}
						>
							<Select 
								control={controlGroupers}
								name="delayed"
								label={translate("Atrasada")}
								options={[
									{ value: ActionDelayed.NO, label: `${translate("Não")}` },
									{ value: ActionDelayed.YES, label: `${translate("Sim")}` }
								]}
								isClearable
								defaultValue={groupersSelect.delayed}
								onChangeCapture={() => buttonSubmitFormGroupersRef.current?.click()}
							/>
						</RCCGrid>

						<RCCGrid
							container
							item
							xs={12}
							sm={6}
							md={12}
							lg={3}
							justifyContent="flex-end"
						>
							<RCCSelectOptionsCard
								defaultValue={myActions ? "S" : "N"}
								options={[{
									value: "N",
									label: translate("Todas ações")
								},{
									value: "S",
									label: translate("Minhas ações")
								}]}
								label="&nbsp;"
								onChange={(e) => {
									setValueFilters("responsible", { value: "", label: ""});
									if(e === "S"){
										setMyActions(true);
										setFilters({
											...filters,
											responsible: user.efa005Id.toString()
										});
										return;
									}
									setMyActions(false);
									setFilters({
										...filters,
										responsible: ""
									});
								}}
							/>
						</RCCGrid>

					</RCCGrid>
					<button 
						aria-label="Submit Form"
						ref={buttonSubmitFormGroupersRef}
						type="submit" 
						style={{ display: "none" }}
					></button>
				</form>				
			
				{/* Groupers */}
				{Groupers}

			</RCCGrid>

			<ModalAddChangeDelete 
				ref={modalAddRef} 
			/>

			<ModalActionsHierarchyMaps 
				ref={modalHierarchyMaps}
				onCallback={({ iniciative, objective }) => {
					if(!modalAddRef.current)
						return;

					modalAddRef.current.openModalAddOrChange(undefined, () => onCallbackAdd(), undefined, undefined, {
						objectiveId: objective,
						iniciativeId: iniciative
					}, undefined);
				}}
			/>
		</>
	);
};

export default ActionsPanel;
